import { useEffect } from "react";

import { usePrefetchQuery, useQueryClient } from "@tanstack/react-query";
import { UIView, useRouter } from "@uirouter/react";
import { useDispatch, useSelector } from "react-redux";

import { swSettings } from "app/common/services/swSettings";
import { PageTopBanner } from "app/components/PageTopBanner/PageTopBanner";
import { FeedbackSideTab } from "app/components/React/sideTabs";
import { useMount } from "app/custom-hooks/useMount";
import { AfterTrialEntryModal } from "app/pages/sales-intelligence/common-components/AfterTrialEntryModal/AfterTrialEntryModal";
import {
    ExtensionBanner,
    isExtensionBannerRendered,
} from "app/pages/sales-intelligence/common-components/ExtensionBanner/ExtensionBanner";
import { IndustryTransitionModalHost } from "app/pages/sales-intelligence/modules/industry/containers/IndustryTransitionModalHost";
import { useIndustryTransitionModalStore } from "app/pages/sales-intelligence/modules/industry/store/useIndustryTransitionModalStore";
import {
    OnboardingContainer,
    USER_CLOSED_AGENT_ACTIVATION,
    USER_COMPLETED_ACTIVATION,
} from "app/pages/sales-intelligence/modules/onboarding";
import { DataCreditsModal } from "app/pages/sales-intelligence/modules/quota/components/DataCreditsModal/DataCreditsModal";
import { QuotaContactAdminModalContainer } from "app/pages/sales-intelligence/modules/quota/containers/QuotaContactAdminModalContainer/QuotaContactAdminModalContainer";
import { lastActivityQueryOptions } from "app/pages/sales-intelligence/query/queries/useLastActivityQuery";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { useDemoModeStore } from "app/pages/sales-intelligence/store/demo-mode/useDemoModeStore";
import { accountManagersQueryOptions } from "app/pages/sales-intelligence/sub-modules/account-manager/queries/useAccountManagersQuery";
import { setIsExtensionBannerVisible } from "app/pages/sales-intelligence/sub-modules/common/store/action-creators";
import { selectIsContactsVisible } from "app/pages/sales-intelligence/sub-modules/contacts-tab/store/selectors";
import ProfileModalContainer from "app/pages/sales-intelligence/sub-modules/profile-modal/ProfileModalContainer";
import { registerWiToSiRedirect } from "app/pages/sales-intelligence/utils/redirectWiToSi";
import { selectImpersonateMode } from "app/selectors/impersonation";
import { PreferencesService } from "app/services/preferences/preferencesService";
import {
    isNoTouch,
    isSI,
    isTraditionalTrialOrAfterTrialUser,
} from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

export const SalesIntelligenceRoot = () => {
    const dispatch = useDispatch();
    const router = useRouter();
    const queryClient = useQueryClient();
    const isContactsVisible = useSelector(selectIsContactsVisible);
    const isImpersonateMode: boolean = useSelector(selectImpersonateMode);

    // Keep reused WI Ad Intelligence components from "escaping" SI: rewrite SI → WI
    // transitions to their SI mirror in the onBefore phase (before WI redirectTo pipelines
    // strip params). Scoped to SI lifecycle instead of the global routerOnBefore.
    useEffect(() => {
        const deregister = registerWiToSiRedirect(router);
        return () => deregister();
    }, [router]);

    const isAppsOnly = salesSettingsHelper.isAppsOnly;

    const isHideAgentActivation: boolean = !!(
        PreferencesService.get(USER_COMPLETED_ACTIVATION) || PreferencesService.get(USER_CLOSED_AGENT_ACTIVATION)
    );

    // Fire on every SI module entry so the BE records user activity even
    // when the user lands on a non-home route (advanced-search, account-
    // overview, etc.). `staleTime: Infinity` keeps it to one request per
    // session; the home page reads the same cache entry via
    // `useLastActivityQuery`.
    usePrefetchQuery(lastActivityQueryOptions);

    // Demo mode is an ephemeral session preview whose lifetime is owned by `DemoModeBanner` (unmounting it exits
    // the demo). Ending any lingering demo on SI (re-)entry is the safety net for a single-spa teardown that
    // drops the container without running React effect cleanups. `exitDemo` is a no-op when nothing is active.
    useMount(() => useDemoModeStore.getState().exitDemo());

    // Same safety net for the impact-report modal: its store is module-scoped, so an admin who left SI
    // with it open would otherwise be met by an account-migration dialog on re-entry.
    useMount(() => useIndustryTransitionModalStore.getState().reset());

    useMount(() => {
        if (isExtensionBannerRendered() && !isContactsVisible) {
            dispatch(setIsExtensionBannerVisible(true));
        }

        // Warm the Account Manager / ASM cache on SI module entry so the
        // "Book a meeting" CTA in any contact-us flow (`ModalContent`,
        // `SuccessBanner`, gain-access, locked overlays) can render
        // synchronously from the React Query cache instead of waiting on a
        // round-trip after the user clicks "Contact us".
        //
        // Gated to paid Touch users with SI access — they are the only
        // segment that actually has a Salesforce Account Manager, so the
        // request is meaningful for them. No-touch and trial / after-trial
        // users do not have an AM and would always receive an empty
        // payload, which would only burn a request and clutter network
        // tabs during local dev.
        const isPaidTouchSIUser =
            isSI(swSettings) && !isNoTouch(swSettings) && !isTraditionalTrialOrAfterTrialUser(swSettings);

        if (isPaidTouchSIUser) {
            void queryClient.prefetchQuery(accountManagersQueryOptions);
        }
    });

    return (
        <>
            {isExtensionBannerRendered() ? <ExtensionBanner /> : null}

            <div className="sw-layout-module-inner">
                <div
                    className="u-flex-row u-flex-column"
                    style={{ flexGrow: 1, overflowX: "hidden", width: "100%" }}
                >
                    <div className="hidden-print">
                        <PageTopBanner />
                    </div>

                    <UIView className="sw-layout-scrollable-element sw-layout-research disable-top-padding use-sticky-css-rendering fadeIn" />
                </div>
            </div>

            {isAppsOnly || isImpersonateMode || isHideAgentActivation ? null : <OnboardingContainer />}

            <AfterTrialEntryModal isImpersonateMode={isImpersonateMode} />

            <ProfileModalContainer />
            <IndustryTransitionModalHost />
            <DataCreditsModal />
            <QuotaContactAdminModalContainer />
            <FeedbackSideTab id="si-feedback-side-tab" />
        </>
    );
};
